/**
 * The background queue that moves capture bytes while the host is still shooting.
 *
 * One capture in flight at a time, started only after interactions settle: the camera screen is
 * the one place jank is unforgivable, and seventeen captures must never become seventeen
 * concurrent uploads fighting the viewfinder for the uplink.
 *
 * SILENT BY CONTRACT. Nothing here throws, retries loudly, or touches UI state. A staged upload
 * that fails leaves no record, and a photo with no record publishes through the direct upload
 * exactly as it did before staging existed — the queue can only ever remove waiting, never add
 * failure. That is also why results are written straight to the wizard store instead of being
 * returned: by the time an upload lands, the component that enqueued it may be long unmounted.
 *
 * The store write is guarded by uri: the record only lands if the draft still holds a photo
 * with this exact uri, so a capture that was retaken or discarded mid-flight cannot deposit a
 * stale entry.
 */
import { vehicleImageService } from '../services/vehicle-image.service'
import { stagedSourceKey } from './listing-photo-storage'
import { useListingWizardStore } from '../stores/listing-wizard.store'
import { scheduleIdleTask } from './idle-callback'

let chain: Promise<void> = Promise.resolve()

export function queueListingPhotoStaging(uri: string): void {
  if (!uri) return
  chain = chain
    .then(
      () =>
        new Promise<void>((resolve) => {
          scheduleIdleTask(resolve, { timeoutMs: 1_000, minimumDelayMs: 250 })
        }),
    )
    .then(async () => {
      // Already staged (same durable uri, e.g. a resumed draft re-entering the step): the
      // object key is content-derived and upsert'd, so re-staging would be harmless — but it
      // would also be a full compress+upload for nothing. The map is keyed by the DIGEST of
      // the uri, never the uri itself: on web the uri is the photo.
      const sourceKey = await stagedSourceKey(uri)
      const state = useListingWizardStore.getState()
      if (state.stagedPhotos[sourceKey]) return

      const staged = await vehicleImageService.stageListingPhoto(uri)
      if (!staged) return

      const draft = useListingWizardStore.getState()
      const stillInDraft = (draft.photos.photos ?? []).some((photo) => photo.uri === uri)
      if (stillInDraft) draft.setStagedPhoto(staged)
    })
    .catch(() => {
      // Swallowed by design; see the module contract above.
    })
}
